import { useCallback, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { useQuery } from "@tanstack/react-query";
import {
  MEDITATION_RECORD_ANALYSIS_DETAIL_ENDPOINT,
  MEDITATION_RECORD_DOMAIN,
  MeditationAnalysisType,
  meditationRecordApi,
} from "api/meditationRecordApi";
import WeekTracking from "./WeekTracking";
import LineChart from "../../common/LineChart";
import BarChart from "../../common/BarChart";
import TaskHeader from "../header/TaskHeader";
import ContentTemplateV2 from "../content/ContentTemplateV2";

const Container = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack
      sx={{
        borderRadius: "12px",
        backgroundColor: "white",
        borderWidth: "1px",
        borderColor: "divider",
        p: "20px",
      }}
      spacing={"20px"}
    >
      <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
        {title}
      </Typography>

      {children}
    </Stack>
  );
};

export default function MeditationRecordMobileStatisticsPage() {
  const user = useRecoilValue(userAtom);

  const [data, setData] = useState<MeditationAnalysisType | undefined>();

  const getAnalysisQueryFn = useCallback(
    () => meditationRecordApi.getAnalysisDetail(user?.userId!!),
    [user?.userId]
  );

  useQuery(
    [MEDITATION_RECORD_DOMAIN, MEDITATION_RECORD_ANALYSIS_DETAIL_ENDPOINT],
    getAnalysisQueryFn,
    {
      enabled: !!user?.userId,
      onSuccess: (data) => {
        setData(data.result);
      },
    }
  );

  function roundUpToNearestTen(num: number) {
    return (Math.ceil(num / 10) + (num % 10 > 0 ? 0 : 1)) * 10;
  }

  return (
    <>
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          taskKey={`meditationRecordStatistics:${user?.userId}`}
          title={`연습 통계`}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        <ContentTemplateV2>
          <Stack spacing={"16px"} sx={{}}>
            <Container title={"이번 주 훈련 목표"}>
              <WeekTracking
                goalDayOfWeekList={data?.goalDayOfWeekList}
                historyList={data?.historyList}
              />
            </Container>
            <Container title={"누적 훈련 시간 (분)"}>
              <LineChart
                id={"cumulative"}
                maxScore={roundUpToNearestTen(
                  Math.max(
                    ...(data?.cumulativeByWeekNumber.map(
                      (each) => each.minutes
                    ) || [])
                  )
                )}
                placeholder={"명상 훈련을 마쳐주세요."}
                data={
                  data?.cumulativeByWeekNumber.map((each) => each.minutes) || []
                }
                xLabel={data?.cumulativeByWeekNumber.map(
                  (each) => `w${each.weekNumber}`
                )}
                {...((data?.cumulativeByWeekNumber || []).length > 0 && {
                  showYRange: true,
                })}
                showMarkValue
                showXAxis
              />
            </Container>
            <Container title={"1회 평균 훈련 시간 (분)"}>
              <BarChart
                id={"average"}
                placeholder={"명상 훈련을 마쳐주세요."}
                maxScore={roundUpToNearestTen(
                  Math.max(
                    ...(data?.averageByWeekNumber.map((each) => each.minutes) ||
                      [])
                  )
                )}
                data={
                  data?.averageByWeekNumber.map((each) => each.minutes) || []
                }
                xLabel={data?.averageByWeekNumber.map(
                  (each) => `w${each.weekNumber}`
                )}
                {...((data?.averageByWeekNumber || []).length > 0 && {
                  showYRange: true,
                })}
                showMarkValue
                showXAxis
              />
            </Container>
          </Stack>
        </ContentTemplateV2>
      </Stack>
    </>
  );
}
